import { useRef, useState } from 'react'
import { HighchartsReactRefObject } from '@highcharts/react'
import { Grid, LinearProgress, useTheme } from '@mui/material'

import useSharedStyles from '../../../../utils/shared.styles'

import BaseChart from './base'
import DialogLoadAll from './dialog-load-all'
import { defaultDataProps, extractSequences, getAlertPlotLines, useFetchChartData } from './helpers'

const BinaryChart = ({
  title,
  deviceId,
  channel,
  createdAt,
  chart,
  alerts,
}: {
  title: string
  deviceId: number
  channel: ChannelType
  createdAt: string
  chart: string
  alerts: IAlertHistory[]
}) => {
  const theme = useTheme()
  const sharedClasses = useSharedStyles
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const chartRef = useRef<HighchartsReactRefObject>(null)
  const { data, isLoading, isBackgroundLoading, fetchData, timeRangeLoaded } = useFetchChartData(deviceId, [channel], createdAt, chart)
  const plotLines = getAlertPlotLines({ alerts, channels: [channel], theme })

  const seriesData = data[0]?.data && data[0]?.data.length > 0 ? extractSequences(data[0].data) : []
  const chartDescription = `Binary state chart showing ${data[0]?.seriesName || 'device status'} over time. The chart displays time ranges when the state was active. Use the range selector buttons to change the time range displayed.`

  const chartData = {
    ...defaultDataProps,
    title: {
      ...defaultDataProps.title,
      text: title,
    },
    accessibility: {
      ...defaultDataProps.accessibility,
      description: chartDescription,
    },
    chart: {
      ...defaultDataProps?.chart,
      type: 'xrange',
      height: 400,
      spacingLeft: 48,
    },
    colors: ['#2D9CDB'],
    rangeSelector: {
      allButtonsEnabled: true,
      buttons: [
        {
          type: 'day',
          count: 1,
          text: '1d',
          title: 'View 1 day',
        },
        {
          type: 'week',
          count: 1,
          text: '1w',
          title: 'View 1 week',
        },
        {
          type: 'month',
          count: 1,
          text: '1m',
          title: 'View 1 month',
        },
        {
          type: 'month',
          count: 3,
          text: '3m',
          title: 'View 3 months',
        },
        {
          type: 'month',
          count: 6,
          text: '6m',
          title: 'View 6 months',
          events: {
            click: async () => {
              if (timeRangeLoaded === '3m') {
                await fetchData('6m')
                setTimeout(() => {
                  // @ts-expect-error - rangeSelector exists
                  chartRef.current?.chart.rangeSelector.clickButton(4, true)
                }, 500)
              }
            },
          },
        },
        {
          type: 'all',
          text: 'All',
          title: 'View all',
          events: {
            click: () => {
              if (timeRangeLoaded !== 'all') setShowDialog(true)
            },
          },
        },
      ],
    },
    yAxis: {
      title: {
        text: '',
      },
      categories: [data[0]?.seriesName],
      minorGridLineWidth: 0,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
    },
    xAxis: {
      crosshair: true,
      plotLines,
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: false,
        },
      },
    },
    navigator: {
      series: {
        type: 'xrange',
        pointWidth: 10,
      },
    },
    series: [
      {
        name: data[0]?.seriesName,
        type: 'xrange',
        data: seriesData,
        turboThreshold: seriesData.length,
        pointWidth: 20,
        showInNavigator: true,
        showInLegend: true,
        dataLabels: {
          enabled: true,
        },
        legendColor: '#2D9CDB',
        tooltip: {
          headerFormat: '<span style="font-size: 0.8em">{point.x:%b %e %Y %l:%M%P} - {point.x2:%b %e %Y %l:%M%P} (UTC)</span><br/>',
          pointFormat: '<span style="color:{point.color}">●</span> {point.yCategory}<br/>',
        },
      },
    ],
  }
  return (
    <Grid container={true} sx={[sharedClasses.centerContentVertical, { flexDirection: 'column' }]}>
      {isBackgroundLoading && !isLoading && <LinearProgress sx={{ width: '850px', marginLeft: '36px' }} />}
      <BaseChart chartData={chartData as unknown as Highcharts.Options} chartRef={chartRef} isLoading={isLoading} />
      <DialogLoadAll
        open={showDialog && timeRangeLoaded !== 'all'}
        onClose={() => setShowDialog(false)}
        onContinue={async () => {
          setShowDialog(false)
          await fetchData('all')
          setTimeout(() => {
            // @ts-expect-error - rangeSelector exists
            chartRef.current?.chart.rangeSelector.clickButton(5, true)
          }, 500)
        }}
      />
    </Grid>
  )
}

export default BinaryChart
